import { useEducationFeature } from 'education-tools'
import { utils as commonUtils } from 'mage-common'
import { selectors as configSelectors } from 'mage-config'
import { actions as coreActions, selectors as coreSelectors, components as coreComponents } from 'mage-core'
import { Framework as MpUiFramework } from 'mage-deps/@klarna/mp-ui'
import { tokenHelperFactory } from 'mage-deps/@mpp/token-helper'
import * as Sentry from 'mage-deps/@sentry/browser'
import { selectors as i18nSelectors } from 'mage-i18n'
import { selectors as stateSelectors, thunks as stateThunks } from 'mage-state'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import React, { useEffect, useState, useMemo } from 'react'
import { connect } from 'react-redux'

import { useAuthenticationContext } from './AuthenticationContext'
import * as pages from './Pages'
import * as a from '../actions'

const checkKeycloak = (healthcheckUrl, { tries = 5, delay = 200 } = {}) => {
  if (!healthcheckUrl) {
    // for backwards-compatibility with missing keycloak healthcheck urls
    return Promise.resolve(true)
  }

  const probeAuthHealthcheck = triesLeft => {
    return fetch(healthcheckUrl, { cache: 'no-cache' })
      .then(response => {
        if (response.status === 200) {
          return true
        } else {
          throw new Error(`Keycloak responded with ${response.status}`)
        }
      })
      .catch(error => {
        if (triesLeft - 1 === 0) {
          error.message = `Keycloak is not online - ${error.message}`
          throw error
        } else {
          // eslint-disable-next-line promise/no-nesting
          return commonUtils.sleep(delay)
            .then(() => probeAuthHealthcheck(triesLeft - 1))
        }
      })
  }

  return probeAuthHealthcheck(tries)
}

const configureSentry = (userId, Sentry) => {
  return Sentry.configureScope(scope => {
    scope.setUser({
      id: userId
    })
  })
}

const createLogger = Sentry => ({
  error: err => {
    Sentry.captureMessage(err.message)
  },
  warn: R.identity
})

const Root = ({
  App,
  clientId,
  designVersion,
  fetchRequester,
  Framework,
  history,
  initialiseKeycloak,
  isAuthenticated,
  keycloakConfig,
  location,
  logout,
  mpApps,
  refreshToken,
  region,
  Sentry,
  setMids,
  t,
  termsLink,
  token
}) => {
  const [error, setError] = useState(undefined)
  const [tokenHelper, setTokenHelper] = useState(undefined)
  const { isAuthenticationRequired } = useAuthenticationContext()
  const logger = createLogger(Sentry)
  const maintenanceModeToggle = useEducationFeature('merchant-portal.maintenance-mode')

  useEffect(() => {
    if (token) {
      const tokenUpdated = async () => {
        const tokenHelper = await tokenHelperFactory(token, {
          shouldLogError: true,
          logger
        })
        setTokenHelper(tokenHelper)
        const mids = tokenHelper.getMidsForClient(clientId)
        setMids(mids)
        const userId = tokenHelper.getUserId()
        await fetchRequester(userId)
        configureSentry(userId, Sentry)
      }
      tokenUpdated()
    }
  }, [token])

  useEffect(() => {
    if (isAuthenticationRequired && !isAuthenticated && maintenanceModeToggle === undefined) {
      const authenticate = async () => {
        try {
          await checkKeycloak(keycloakConfig.healthcheck, {
            tries: keycloakConfig.connectAttempts,
            delay: keycloakConfig.connectDelay
          })
          initialiseKeycloak()
          setInterval(refreshToken, 5000)
        } catch (err) {
          setError(err)
        }
      }
      authenticate()
    }
  }, [isAuthenticationRequired, isAuthenticated, keycloakConfig, maintenanceModeToggle])

  const currentYear = new Date().getFullYear()
  const footerConfig = useMemo(() => {
    return {
      text: t(`core.footer.copyright.${region}`, { currentYear }, {}, ''),
      links: [
        { linkText: t('core.footer.terms'), path: termsLink }
      ]
    }
  }, [currentYear, region, termsLink, t])

  if (error) {
    throw error
  } else if (maintenanceModeToggle) {
    return (
      <pages.Maintenance
        footerConfig={footerConfig}
        designVersion={designVersion}
        until={maintenanceModeToggle.until}
        t={t}
      />
    )
  } else if (!isAuthenticationRequired) {
    return (
      <pages.Public
        App={App}
        history={history}
        designVersion={designVersion}
        location={location}
        footerConfig={footerConfig}
        t={t}
        clientId={clientId}
        Framework={Framework}
      />
    )
  } else if (!isAuthenticated || !tokenHelper) {
    return (
      <pages.Unauthenticated
        footerConfig={footerConfig}
      />
    )
  } else {
    return (
      <coreComponents.TokenHelperContext.Provider value={tokenHelper}>
        <pages.App
          App={App}
          mpApps={mpApps(tokenHelper)}
          Framework={Framework}
          t={t}
          history={history}
          designVersion={designVersion}
          location={location}
          footerConfig={footerConfig}
          clientId={clientId}
          onLogoutClick={logout}
        />
      </coreComponents.TokenHelperContext.Provider>
    )
  }
}

Root.propTypes = {
  App: PropTypes.any,
  clientId: PropTypes.string.isRequired,
  designVersion: PropTypes.string,
  fetchRequester: PropTypes.func.isRequired,
  history: PropTypes.object,
  initialiseKeycloak: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  keycloakConfig: PropTypes.shape({
    healthcheck: PropTypes.string.isRequired,
    connectAttempts: PropTypes.number,
    connectDelay: PropTypes.number
  }).isRequired,
  Framework: PropTypes.any,
  location: PropTypes.object,
  logout: PropTypes.func.isRequired,
  mpApps: PropTypes.func.isRequired,
  refreshToken: PropTypes.func.isRequired,
  region: PropTypes.oneOf(['local', 'eu', 'us', 'oc']).isRequired,
  Sentry: PropTypes.shape({
    configureScope: PropTypes.func,
    captureMessage: PropTypes.func
  }),
  setMids: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  termsLink: PropTypes.string.isRequired,
  token: PropTypes.string
}

Root.defaultProps = {
  Framework: MpUiFramework,
  isAuthenticated: false,
  Sentry
}

export default R.compose(
  connect(
    state => ({
      clientId: coreSelectors.clientId(state),
      designVersion: coreSelectors.getDesignVersion(state),
      isAuthenticated: coreSelectors.isAuthenticated(state),
      keycloakConfig: stateSelectors.keycloakConfig(state),
      location: state.router.location,
      mpApps: stateSelectors.getUserAccessibleApps(state),
      region: configSelectors.region(state),
      t: i18nSelectors.translator(state),
      termsLink: configSelectors.terms(state).url,
      token: coreSelectors.getAccessToken(state)
    }),
    {
      fetchRequester: a.fetchRequester,
      initialiseKeycloak: stateThunks.initialiseKeycloak,
      logout: stateThunks.logout,
      refreshToken: stateThunks.refreshToken,
      setMids: coreActions.mids.set
    }
  )
)(Root)

export {
  Root
}
